import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { Callout } from '../../Callout/Callout';
import type { LotteryName } from '../../lottery-purchases/types';
import { Coupon } from '../../svg/Coupon/Coupon';

type LotteryPurchasePodStatus = 'default' | 'success' | 'info' | 'warning';

/**
 * LotteryPurchasePodLabel is a component that displays optional label text in the top-right corners of the pod.
 * Extends Callout component with predefined variant (accent).
 * @returns Callout component
 */
function Label({ className, variant = 'accent', ...props }: ComponentProps<typeof Callout>) {
  return <Callout variant={variant} className={twMerge('absolute top-0 right-0 rounded-r-none', className)} {...props} />;
}

/**
 * LotteryPurchasePodDetail is a component that displays optional details inside the body of the pod.
 * Extends HTMLParagraphElement with predefined className (paragraph-s).
 * @returns HTMLParagraphElement component
 */
function Detail({ className, ...props }: ComponentProps<'p'>) {
  return <p className={twMerge('paragraph-s', className)} {...props} />;
}

/**
 * LotteryPurchasePodContent is a component that displays content inside the body of the pod.
 * Some content is already provided by default - translated lottery name and coupon icon with lottery background.
 * Extends HTMLDivElement with predefined className.
 *
 * @param lotteryName - One of the supported lottery names to resolve name and icon background color.
 * @returns HTMLDivElement component
 */
function Content({
  lotteryName,
  className,
  children,
  ...props
}: ComponentProps<'div'> & {
  lotteryName: LotteryName;
}) {
  const t = useTranslations(`lotteries.${lotteryName}`);
  return (
    <div className={twMerge('p-5 flex items-center gap-5', className)} {...props}>
      <div className={`size-10.5 rounded-full flex items-center justify-center shrink-0 bg-${lotteryName.toLowerCase()}`}>
        <Coupon className="w-6.5 h-7 text-lottery-pod-icon" />
      </div>
      <div className="w-full grid grid-cols-[1fr,auto] gap-x-5 justify-between">
        <p className="paragraph-strong col-span-2">{t('name')}</p>
        {children}
      </div>
    </div>
  );
}

/**
 * LotteryPurchasePodStatus is a component that displays a status message with a background color.
 * Extends HTMLDivElement component
 * @param status - The status of the component. Can be one of 'default', 'success', 'info', or 'warning'.
 * @returns HTMLDivElement
 */
function Status({ status, className, children, ...props }: ComponentProps<'div'> & { status: LotteryPurchasePodStatus }) {
  const background = {
    default: 'bg-container',
    success: 'bg-success-background',
    info: 'bg-info-background',
    warning: 'bg-warning-background',
  } satisfies Record<LotteryPurchasePodStatus, string>;

  return (
    <div className={twMerge('px-4 py-2', background[status], className)} {...props}>
      <p className="paragraph-s flex">{children}</p>
    </div>
  );
}

/**
 * LotteryPurchasePod is a component that displays a lottery purchase pod.
 * Extends HTMLDivElement component
 * @returns HTMLDivElement
 */
function LotteryPurchasePod({ className, ...props }: ComponentProps<'div'>) {
  return <div className={twMerge('isolate relative bg-background border border-border rounded-lg overflow-hidden', className)} {...props} />;
}

LotteryPurchasePod.Label = Label;
LotteryPurchasePod.Detail = Detail;
LotteryPurchasePod.Content = Content;
LotteryPurchasePod.Status = Status;

export { LotteryPurchasePod };
export type { LotteryPurchasePodStatus };
